import { z } from 'zod';
import { AdfDocumentSchema } from './adf.js';
import { AccountIdSchema, DateOnlySchema, IssueKeySchema, ProgressSchema, TimestampSchema, } from './common.js';
/** Колонки таблицы задач, по которым разрешена сортировка. */
export const ISSUE_SORT_FIELDS = [
    'key',
    'summary',
    'type',
    'status',
    'assignee',
    'priority',
    'updated',
    'duedate',
];
export const IssueSortFieldSchema = z.enum(ISSUE_SORT_FIELDS);
export const SortDirectionSchema = z.enum(['asc', 'desc']);
/**
 * Запрос списка задач. Пустой projectId означает режим «Все разделы» — тогда в таблице
 * появляется колонка раздела.
 */
export const IssueListQuerySchema = z.object({
    projectId: z.string().optional(),
    /**
     * Набор разделов. Живёт отдельно от `projectId`: одиночный задаёт режим таблицы («Раздел»
     * против «Все разделы») и обновление кэша одного проекта, а набор нужен там, где раздел —
     * такой же фильтр, как статус или тип: в окне подбора задач в план.
     */
    projectIds: z.array(z.string()).max(50).optional(),
    /** Поиск по ключу и названию. */
    q: z.string().trim().max(200).optional(),
    statusIds: z.array(z.string()).max(50).optional(),
    assigneeIds: z.array(z.string()).max(50).optional(),
    /** Значение 'unassigned' отбирает задачи без исполнителя. */
    unassigned: z.boolean().optional(),
    typeIds: z.array(z.string()).max(50).optional(),
    priorityIds: z.array(z.string()).max(50).optional(),
    dueFrom: DateOnlySchema.optional(),
    dueTo: DateOnlySchema.optional(),
    /**
     * Показывать только завершённые задачи. По умолчанию их, наоборот, не видно: таблица о том,
     * что в работе, а закрытых за год накапливается больше, чем открытых.
     */
    doneOnly: z.boolean().default(false),
    /**
     * Только задачи этого уровня иерархии. Нужен выбору родителя для подзадачи: её родитель —
     * обычная задача, а не эпик и не другая подзадача.
     */
    hierarchyLevel: z.number().int().min(-1).max(10).optional(),
    /**
     * Режим дерева: подзадача, чей родитель тоже прошёл отбор, едет в `children` его строки,
     * а не отдельной строкой. Нужен таблице; окну подбора в план удобнее плоский список.
     */
    tree: z.boolean().default(false),
    sort: IssueSortFieldSchema.default('updated'),
    dir: SortDirectionSchema.default('desc'),
    /** Курсор бесконечной прокрутки; непрозрачен для клиента. */
    cursor: z.string().max(512).optional(),
    limit: z.number().int().min(1).max(100).default(50),
});
// ── Настройки таблицы ────────────────────────────────────────────────────────
/**
 * Колонки таблицы задач. Раздел показывается только в режиме «Все разделы» и не сортируется —
 * порядок по нему совпал бы с порядком по ключу. Остальные колонки сортируемы,
 * их имена совпадают с полями сортировки.
 */
export const TABLE_COLUMNS = [
    'section',
    'key',
    'summary',
    'type',
    'status',
    'assignee',
    'priority',
    'updated',
    'duedate',
];
export const TableColumnSchema = z.enum(TABLE_COLUMNS);
/**
 * Название задачи скрыть нельзя: без него строка перестаёт быть строкой задачи.
 * Это единственное ограничение — остальные колонки пользователь волен убрать.
 */
export const REQUIRED_TABLE_COLUMN = 'summary';
export const COLUMN_MIN_WIDTH = 64;
export const COLUMN_MAX_WIDTH = 640;
/** Ширины из макета — те же значения, что стояли в токене `--table-columns`. */
export const DEFAULT_COLUMN_WIDTHS = {
    section: 140,
    key: 96,
    /*
     * Название тянется на весь свободный остаток, ширина задаёт только его минимум. Значение
     * подобрано так, чтобы полный набор колонок с разделом помещался в 1440 px без прокрутки.
     */
    summary: 200,
    type: 116,
    status: 132,
    assignee: 172,
    priority: 108,
    updated: 116,
    duedate: 104,
};
/**
 * Личные настройки таблицы. Хранятся за пользователем, а не за тенантом: ширины колонок —
 * дело привычки и экрана, а не общая договорённость команды.
 */
export const TableSettingsSchema = z.object({
    /** Порядок колонок. Неизвестные имена отбрасываются, новые дописываются в конец. */
    order: z.array(TableColumnSchema).max(TABLE_COLUMNS.length),
    hidden: z.array(TableColumnSchema).max(TABLE_COLUMNS.length),
    /** Ширины в пикселях; колонка без записи показывается со значением из макета. */
    widths: z.partialRecord(TableColumnSchema, z.number().int().min(COLUMN_MIN_WIDTH).max(COLUMN_MAX_WIDTH)),
    /**
     * Состояние переключателя «Завершённые». Со значением по умолчанию: настройки, записанные
     * до появления поля, не должны обнулять уже настроенные колонки.
     */
    doneOnly: z.boolean().default(false),
});
export const DEFAULT_TABLE_SETTINGS = {
    order: [...TABLE_COLUMNS],
    hidden: [],
    widths: {},
    doneOnly: false,
};
/**
 * Приведение настроек к рабочему виду.
 *
 * Настройки живут в базе как jsonb и переживают изменения состава колонок, поэтому доверять
 * им нельзя: разбираем схемой, лишнее выбрасываем, недостающее достраиваем. Так же
 * обрабатывается и приходящее от клиента — сохранять заведомо сломанный набор незачем.
 */
export function normalizeTableSettings(value) {
    const parsed = TableSettingsSchema.safeParse(value);
    if (!parsed.success)
        return DEFAULT_TABLE_SETTINGS;
    const seen = new Set();
    const order = [];
    for (const column of parsed.data.order) {
        if (seen.has(column))
            continue;
        seen.add(column);
        order.push(column);
    }
    // Колонка, появившаяся в новой версии, встаёт в конец, а не пропадает из настроенной таблицы.
    for (const column of TABLE_COLUMNS) {
        if (!seen.has(column))
            order.push(column);
    }
    const hidden = [...new Set(parsed.data.hidden)].filter((column) => column !== REQUIRED_TABLE_COLUMN);
    return { order, hidden, widths: parsed.data.widths, doneOnly: parsed.data.doneOnly };
}
/** Колонки в пользовательском порядке без скрытых. Раздел добавляется только в режиме «Все». */
export function visibleTableColumns(settings, options) {
    return settings.order.filter((column) => !settings.hidden.includes(column) && (column !== 'section' || options.showSection));
}
export function tableColumnWidth(settings, column) {
    return settings.widths[column] ?? DEFAULT_COLUMN_WIDTHS[column];
}
/**
 * Правка задачи. Каждое поле необязательно — отправляем только изменённое.
 * `ifUpdated` — значение поля updated, на котором пользователь начал редактирование:
 * если в Jira оно уже другое, сервер отвечает ISSUE_CONFLICT и правки не применяются.
 */
export const IssueUpdateSchema = z
    .object({
    summary: z.string().trim().min(1).max(255).optional(),
    description: AdfDocumentSchema.nullable().optional(),
    issueTypeId: z.string().optional(),
    priorityId: z.string().nullable().optional(),
    /** Исполнитель задаётся accountId; null снимает исполнителя. */
    assigneeAccountId: AccountIdSchema.nullable().optional(),
    duedate: DateOnlySchema.nullable().optional(),
    progress: ProgressSchema.nullable().optional(),
    /**
     * Новый родитель подзадачи — задача того же раздела. Снять родителя этим полем нельзя:
     * подзадача без родителя — уже задача, и это перенос, а не правка (см. `IssueConvert`).
     */
    parentKey: IssueKeySchema.optional(),
    ifUpdated: TimestampSchema,
})
    .refine((value) => Object.keys(value).some((key) => key !== 'ifUpdated'), 'нужно передать хотя бы одно изменяемое поле');
/**
 * Перенос между уровнями: задача становится подзадачей или подзадача — задачей.
 *
 * В Jira это не правка поля, а перенос — Jira сама подбирает статус, если у подзадач и задач
 * разные процессы, и выполняет его фоновым заданием. Поэтому отдельный запрос, а не часть
 * `IssueUpdate`: у него своё ожидание и свои отказы.
 */
export const IssueConvertSchema = z.object({
    /** Тип после переноса: подзадачи — если задача уходит под родителя, задачи — если обратно. */
    issueTypeId: z.string().trim().min(1),
    /** Родитель будущей подзадачи; при переносе в задачи — null. */
    parentKey: IssueKeySchema.nullable(),
    ifUpdated: TimestampSchema,
});
/**
 * Создание задачи. Раздел и тип обязательны — без них Jira не примет запрос; остальное
 * необязательно и опускается, когда пользователь оставил поле пустым: так задача получает
 * значения по умолчанию своей схемы, а не наши подставленные.
 */
export const IssueCreateSchema = z.object({
    projectId: z.string().trim().min(1),
    issueTypeId: z.string().trim().min(1),
    /**
     * Родитель: обязателен для подзадачи, у задачи эпика — сам эпик. Подзадача живёт в разделе
     * родителя, поэтому `projectId` должен совпадать с его разделом — иначе Jira откажет.
     */
    parentKey: IssueKeySchema.optional(),
    summary: z.string().trim().min(1).max(255),
    description: AdfDocumentSchema.nullable().optional(),
    priorityId: z.string().nullable().optional(),
    assigneeAccountId: AccountIdSchema.nullable().optional(),
    duedate: DateOnlySchema.nullable().optional(),
    progress: ProgressSchema.nullable().optional(),
});
/**
 * Значения, доступные при создании задачи в выбранном разделе.
 *
 * Отличается от `IssueFieldOptions` тем, что задачи ещё нет: типы и назначаемые исполнители
 * запрашиваются по разделу, а не по ключу. Когда выбран тип, добавляется `requiredUnsupported` —
 * обязательные поля экрана создания, которых нет в форме Jisy.
 */
export const IssueCreateOptionsSchema = z.object({
    issueTypes: z.array(z.object({
        id: z.string(),
        name: z.string(),
        iconUrl: z.string().nullable(),
        /**
         * Уровень иерархии Jira: −1 — подзадача, 0 — обычная задача, 1 и выше — эпик и надстройки
         * над ним. По нему выбирается значение по умолчанию: схема проекта нередко ставит эпик
         * первым, а форма не должна подсовывать его тому, кто просто заводит задачу.
         */
        hierarchyLevel: z.number().int().nullable(),
        /** Подзадача: форма спрашивает для неё родительскую задачу. */
        subtask: z.boolean(),
    })),
    priorities: z.array(z.object({ id: z.string(), name: z.string(), rank: z.number().int().nullable() })),
    assignees: z.array(z.object({
        accountId: AccountIdSchema,
        displayName: z.string(),
        avatarUrl: z.string().nullable(),
    })),
    /**
     * Обязательные поля выбранного типа, которые форма не заполняет (компоненты, версии,
     * свои поля проекта). Заполнен — создавать нечем, задачу заводят в Jira.
     */
    requiredUnsupported: z.array(z.object({ fieldId: z.string(), name: z.string() })),
    /** Настроено ли поле процента: без него ползунок процента в форме показывать незачем. */
    hasProgressField: z.boolean(),
    /** Списки, которых Jira не дала: показываются как недоступные, а не пустые. */
    readOnly: z.array(z.string()),
});
export const TransitionRequestSchema = z.object({
    transitionId: z.string().min(1),
    ifUpdated: TimestampSchema,
});
export const CommentCreateSchema = z.object({
    body: AdfDocumentSchema,
});
/**
 * Ответ при конфликте версий: показывается карточка «Кто-то изменил эту задачу»
 * со сравнением значений.
 */
export const IssueConflictSchema = z.object({
    issueKey: z.string(),
    updatedAt: TimestampSchema,
    updatedBy: z
        .object({
        accountId: AccountIdSchema,
        displayName: z.string(),
        avatarUrl: z.string().nullable(),
    })
        .nullable(),
    /** Поля, которые разошлись: значение у пользователя и значение в Jira. */
    diff: z.array(z.object({
        field: z.string(),
        mine: z.unknown(),
        theirs: z.unknown(),
    })),
});
/**
 * Значения, доступные конкретной задаче: типы её проекта, приоритеты сайта и пользователи,
 * которых Jira разрешает назначить именно на неё.
 */
export const IssueFieldOptionsSchema = z.object({
    /**
     * Все типы проекта, подзадачи тоже. Карточка выбирает из них сама: тип подзадачи меняется
     * только на тип подзадачи, а строка «добавить подзадачу» берёт типы на уровень ниже задачи.
     */
    issueTypes: z.array(z.object({
        id: z.string(),
        name: z.string(),
        iconUrl: z.string().nullable(),
        subtask: z.boolean(),
        hierarchyLevel: z.number().int(),
    })),
    priorities: z.array(z.object({ id: z.string(), name: z.string(), rank: z.number().int().nullable() })),
    assignees: z.array(z.object({
        accountId: AccountIdSchema,
        displayName: z.string(),
        avatarUrl: z.string().nullable(),
    })),
    /** Поля, которые Jira не даёт править на этой задаче: показываются только для чтения. */
    readOnly: z.array(z.string()),
});
// ── Вложения ─────────────────────────────────────────────────────────────────
/**
 * Потолок вложения. Совпадает со значением по умолчанию в Jira Cloud: брать больше бессмысленно —
 * файл всё равно вернётся отказом, только после того, как его успеют выгрузить на сервер.
 * Администратор сайта потолок в Jira может поднять, но узнать об этом можно лишь по отказу.
 */
export const ISSUE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
/**
 * Тип содержимого вложения. Приходит параметром, а не заголовком запроса: тело уезжает байтами
 * (`application/octet-stream`), иначе файл с типом `application/json` перехватил бы общий разбор
 * JSON и до маршрута дошёл бы объект вместо буфера.
 */
export const AttachmentMimeSchema = z
    .string()
    .trim()
    .max(200)
    .regex(/^[\w.+-]+\/[\w.+-]+$/u, 'недопустимый тип содержимого');
/**
 * Имя вложения. Приходит от браузера отдельным параметром, поэтому проверяется здесь: разделители
 * пути и управляющие символы в multipart к Jira попасть не должны.
 */
export const AttachmentFilenameSchema = z
    .string()
    .trim()
    .min(1)
    .max(255)
    .refine((value) => !/[/\\\u0000-\u001f]/u.test(value), 'недопустимое имя файла');
//# sourceMappingURL=issues.js.map